import { For, Show, createResource, createSignal } from "solid-js";
import { A, useNavigate } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import FirmLogoCard from "../components/FirmLogoCard";
import PageHeader from "../components/PageHeader";
import { needsFirm } from "../components/FirmPicker";
import {
  api,
  failureSentence,
  type OperatorEnvironment,
  type OperatorListItem,
  type OperatorRateLimitTier,
} from "../lib/api";
import { firmId } from "../lib/firm";
import { canMutate, useSession } from "../lib/session";

const OPERATOR_ENVIRONMENTS = [
  "sandbox",
  "development",
  "staging",
  "production",
] as const;
const OPERATOR_RATE_LIMIT_TIERS = [
  "standard",
  "pro",
  "enterprise",
  "unlimited",
] as const;

interface CreateOperatorForm {
  name: string;
  slug: string;
  environment: OperatorEnvironment;
  contact_email: string;
  rate_limit_tier: OperatorRateLimitTier;
}

export default function OperatorsList() {
  const session = useSession();
  const navigate = useNavigate();
  const [search, setSearch] = createSignal("");
  const [createForm, setCreateForm] = createSignal<CreateOperatorForm>({
    name: "",
    slug: "",
    environment: "sandbox",
    contact_email: "",
    rate_limit_tier: "standard",
  });
  const [creating, setCreating] = createSignal(false);
  const [createError, setCreateError] = createSignal<string | null>(null);

  const [page, { refetch }] = createResource(
    () => !needsFirm(session.admin()?.role ?? "") && { firm: firmId() },
    () => api.listOperators(),
  );
  const matching = (operators: OperatorListItem[]) => {
    const q = search().trim().toLowerCase();
    return q
      ? operators.filter((op) =>
          [op.name, op.slug, op.operator_id, op.org_id, op.api_key_prefix ?? ""]
            .some((field) => field.toLowerCase().includes(q)),
        )
      : operators;
  };

  const canCreate = () => {
    const admin = session.admin();
    return admin ? canMutate(admin.role) : false;
  };

  function setCreateField<K extends keyof CreateOperatorForm>(
    field: K,
    value: CreateOperatorForm[K],
  ) {
    setCreateForm((current) => ({ ...current, [field]: value }));
    setCreateError(null);
  }

  async function onCreateOperator(ev: Event) {
    ev.preventDefault();
    const admin = session.admin();
    if (!admin) {
      setCreateError("admin session is still loading");
      return;
    }

    const form = createForm();
    if (needsFirm(admin.role)) {
      setCreateError("choose the firm the operator belongs to");
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const operator = await api.createOperator({
        name: form.name,
        slug: form.slug,
        environment: form.environment,
        contact_email: form.contact_email.trim() || null,
        rate_limit_tier: form.rate_limit_tier,
      });
      refetch();
      navigate(`/operators/${operator.operator_id}`);
    } catch (e) {
      setCreateError(failureSentence(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <PageHeader title="Operators" subtitle="Your integration identities" />

      <Show keyed when={!needsFirm(session.admin()?.role ?? "") && { firm: firmId() }}>
        {(chosen) => <FirmLogoCard orgId={chosen.firm} canEdit={canCreate()} />}
      </Show>

      <Show when={canCreate()}>
        <div class="card mb-4">
          <div class="card-header">
            <h2 class="text-sm font-semibold">Create operator</h2>
          </div>
          <div class="card-body">
            <form class="space-y-3" onSubmit={onCreateOperator}>
              <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                <div>
                  <label class="label" for="operator-name">
                    Label
                  </label>
                  <input
                    id="operator-name"
                    class="input"
                    value={createForm().name}
                    onInput={(e) =>
                      setCreateField("name", e.currentTarget.value)
                    }
                  />
                  <p class="mt-1 text-xs text-slate-500">
                    For your staff. Traders see your firm's name.
                  </p>
                </div>
                <div>
                  <label class="label" for="operator-slug">
                    Slug
                  </label>
                  <input
                    id="operator-slug"
                    class="input font-mono text-xs"
                    value={createForm().slug}
                    onInput={(e) =>
                      setCreateField("slug", e.currentTarget.value)
                    }
                  />
                </div>
                <div>
                  <label class="label" for="operator-environment">
                    Environment
                  </label>
                  <select
                    id="operator-environment"
                    class="select"
                    value={createForm().environment}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      if (isOperatorEnvironment(value)) {
                        setCreateField("environment", value);
                      }
                    }}
                  >
                    <For each={OPERATOR_ENVIRONMENTS}>
                      {(environment) => (
                        <option value={environment}>{environment}</option>
                      )}
                    </For>
                  </select>
                </div>
                <div>
                  <label class="label" for="operator-rate-tier">
                    Rate tier
                  </label>
                  <select
                    id="operator-rate-tier"
                    class="select"
                    value={createForm().rate_limit_tier}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      if (isOperatorRateLimitTier(value)) {
                        setCreateField("rate_limit_tier", value);
                      }
                    }}
                  >
                    <For each={OPERATOR_RATE_LIMIT_TIERS}>
                      {(tier) => <option value={tier}>{tier}</option>}
                    </For>
                  </select>
                </div>
                <div>
                  <label class="label" for="operator-contact-email">
                    Contact email
                  </label>
                  <input
                    id="operator-contact-email"
                    class="input"
                    type="email"
                    value={createForm().contact_email}
                    onInput={(e) =>
                      setCreateField("contact_email", e.currentTarget.value)
                    }
                  />
                </div>
              </div>
              <Show when={createError()}>
                <p class="text-sm text-red-600">{createError()}</p>
              </Show>
              <button type="submit" class="btn-primary" disabled={creating()}>
                {creating() ? "Creating…" : "Create operator"}
              </button>
            </form>
          </div>
        </div>
      </Show>

      <div class="card">
        <div class="card-body border-b border-slate-200">
          <label class="label" for="operator-search">
            Search operators
          </label>
          <input
            id="operator-search"
            class="input"
            type="search"
            placeholder="Label, slug, operator id, org id, or API key prefix"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <AsyncResource resource={page}>
          {(data) => (
            <>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Status</th>
                    <th>Environment</th>
                    <th>API key prefix</th>
                    <th>Webhook</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={matching(data().operators)}>
                    {(op) => (
                      <tr>
                        <td>
                          <div class="font-medium">{op.name}</div>
                          <div class="text-xs text-slate-500">{op.slug}</div>
                          <div class="mt-1 font-mono text-[11px] leading-5 text-slate-400">
                            {op.operator_id}
                          </div>
                          <div class="font-mono text-[11px] leading-5 text-slate-400">
                            org {op.org_id}
                          </div>
                        </td>
                        <td>
                          <span class={statusClass(op.status)}>{op.status}</span>
                        </td>
                        <td>{op.environment}</td>
                        <td class="font-mono text-xs">
                          {op.api_key_prefix ?? "—"}
                        </td>
                        <td class="text-xs">
                          {op.webhook_url ? (
                            <span class="font-mono">{op.webhook_url}</span>
                          ) : (
                            <span class="text-slate-400">not set</span>
                          )}
                        </td>
                        <td>
                          <A
                            class="text-sm underline"
                            href={`/operators/${op.operator_id}`}
                          >
                            Manage
                          </A>
                        </td>
                      </tr>
                    )}
                  </For>
                  {matching(data().operators).length === 0 && (
                    <tr>
                      <td colSpan={6} class="text-center text-slate-500 py-6">
                        {search().trim()
                          ? "No operators match this search."
                          : "No operators visible to this admin."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </AsyncResource>
      </div>
    </>
  );
}

function isOperatorEnvironment(value: string): value is OperatorEnvironment {
  return OPERATOR_ENVIRONMENTS.some((environment) => environment === value);
}

function isOperatorRateLimitTier(
  value: string,
): value is OperatorRateLimitTier {
  return OPERATOR_RATE_LIMIT_TIERS.some((tier) => tier === value);
}


function statusClass(status: string): string {
  switch (status) {
    case "active":
      return "status-active";
    case "pending":
      return "status-pending";
    case "suspended":
      return "status-suspended";
    case "terminated":
      return "status-neutral";
    default:
      return "status-neutral";
  }
}
